import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Loader, Printer } from "lucide-react";
import Api from "../Api/Api.json";

export default function PaymentSuccess() {
  const [isLoading, setIsLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'successful' or 'failure'
  const [verificationMessage, setVerificationMessage] = useState('');
  const navigate = useNavigate();

  // Determine environment based on URL
  const getEnvironment = () => {
    const hostname = window.location.hostname;
    // Check if it's localhost or 127.0.0.1 (development)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'development';
    }
    // Otherwise it's production
    return 'production';
  };

  const environment = getEnvironment();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const extractedOrderId = params.get("order_id");
    
    if (extractedOrderId) {
      setOrderId(extractedOrderId);
    }

    // Get shipping info from localStorage
    const storedShippingInfo = localStorage.getItem('shipping_info');
    let shippingData = null;
    if (storedShippingInfo) {
      shippingData = JSON.parse(storedShippingInfo);
      setShippingInfo(shippingData);
    }

    // Get user_id from localStorage
    const storedInfo = localStorage.getItem("upskill_user_info");
    let userId = null;
    if (storedInfo !== null) {
      const userInfo = JSON.parse(storedInfo);
      userId = userInfo.id;
    }

    // Verify payment with API
    if (extractedOrderId && userId) {
      const body = {
        task: "shurjopayVerifyPayment",
        order_id: extractedOrderId,
        environment: environment,
        user_id: userId
      };

      const headers = { "Content-Type": "application/json" };
      axios
        .post(Api.BKASH_API, body, { headers })
        .then((response) => {
          const status = response.data;
          setPaymentStatus(status);
          
          if (status === 'successful') {
            setVerificationMessage('Payment processed.');
            setPaymentDetails({
              orderId: extractedOrderId,
              amount: shippingData?.amount,
              date: new Date().toLocaleString(),
            });
          } else if (status === 'failure') {
            setVerificationMessage('Payment failed, probably from your financial institution.');
          } else {
            setVerificationMessage('Unable to verify payment status.');
            setPaymentStatus('failure');
          }
        })
        .catch((error) => {
          console.error("Error verifying payment:", error);
          setPaymentStatus('failure');
          setVerificationMessage('Payment failed, probably from your financial institution.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // If no order_id or user_id, set as failure
      setPaymentStatus('failure');
      setVerificationMessage('Payment failed, probably from your financial institution.');
      setIsLoading(false);
    }
  }, []);

  const handlePrintInvoice = () => {
    if (paymentDetails && shippingInfo) {
      const printContent = `
        <html>
          <head>
            <title>Invoice - Payment Receipt</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .invoice-header { text-align: center; margin-bottom: 30px; }
              .invoice-header h1 { color: #2563eb; margin: 0; }
              .invoice-section { margin-bottom: 20px; }
              .invoice-section h2 { border-bottom: 2px solid #e5e7eb; padding-bottom: 10px; }
              .info-row { display: flex; justify-content: space-between; margin: 10px 0; }
              .info-label { font-weight: bold; color: #4b5563; }
              .info-value { color: #111827; }
              .total-box { background: #eff6ff; padding: 15px; border-radius: 8px; margin-top: 20px; }
              .footer { margin-top: 40px; text-align: center; color: #6b7280; font-size: 12px; }
              @media print {
                body { padding: 0; }
                .no-print { display: none; }
              }
            </style>
          </head>
          <body>
            <div class="invoice-header">
              <h1>Spoken English - Payment Invoice</h1>
              <p>Invoice Receipt</p>
            </div>
            
            <div class="invoice-section">
              <h2>Payment Information</h2>
              <div class="info-row">
                <span class="info-label">Order ID:</span>
                <span class="info-value">${paymentDetails.orderId}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Amount:</span>
                <span class="info-value">৳${paymentDetails.amount?.toLocaleString()}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Date:</span>
                <span class="info-value">${paymentDetails.date}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Method:</span>
                <span class="info-value">ShurjoPay</span>
              </div>
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span class="info-value" style="color: #10b981; font-weight: bold;">Success</span>
              </div>
            </div>

            <div class="invoice-section">
              <h2>Shipping Information</h2>
              ${shippingInfo.name ? `<div class="info-row">
                <span class="info-label">Name:</span>
                <span class="info-value">${shippingInfo.name}</span>
              </div>` : ''}
              ${shippingInfo.email ? `<div class="info-row">
                <span class="info-label">Email:</span>
                <span class="info-value">${shippingInfo.email}</span>
              </div>` : ''}
              ${shippingInfo.city ? `<div class="info-row">
                <span class="info-label">City:</span>
                <span class="info-value">${shippingInfo.city}</span>
              </div>` : ''}
              <div class="info-row">
                <span class="info-label">Shipping Address:</span>
                <span class="info-value" style="text-align: right; max-width: 60%;">${shippingInfo.address}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Phone Number:</span>
                <span class="info-value">${shippingInfo.phone}</span>
              </div>
            </div>

            <div class="total-box">
              <div class="info-row">
                <span class="info-label" style="font-size: 18px;">Total Amount:</span>
                <span class="info-value" style="font-size: 20px; font-weight: bold; color: #2563eb;">৳${paymentDetails.amount?.toLocaleString()}</span>
              </div>
            </div>

            <div class="footer">
              <p>Thank you for your purchase!</p>
              <p>Spoken English - Your Learning Companion</p>
              <p>This is an automated invoice generated on ${new Date().toLocaleString()}</p>
            </div>
          </body>
        </html>
      `;
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Processing payment result...</p>
        </div>
      </div>
    );
  }

  const isSuccessful = paymentStatus === 'successful';
  const isFailure = paymentStatus === 'failure';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-4 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 max-w-md w-full text-center">
        {/* Status Icon */}
        <div className="mb-6 flex justify-center">
          {isSuccessful && (
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          )}
          {isFailure && (
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
          )}
        </div>

        {/* Status Message */}
        <h2 className="text-3xl font-bold mb-2">
          {isSuccessful && <span className="text-green-600">Payment Successful!</span>}
          {isFailure && <span className="text-red-600">Payment Failed</span>}
        </h2>

        {verificationMessage && (
          <p className={`text-gray-600 mb-8 ${isSuccessful ? 'text-green-700' : 'text-red-700'}`}>
            {verificationMessage}
          </p>
        )}

        {orderId && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Order ID:</p>
            <p className="text-lg font-semibold text-gray-800">{orderId}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {isSuccessful && paymentDetails && shippingInfo && (
            <button
              onClick={handlePrintInvoice}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <Printer className="w-5 h-5" />
              <span>Print Invoice</span>
            </button>
          )}
          {isSuccessful && (
            <button
              onClick={() => navigate("/payment")}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              Make Another Payment
            </button>
          )}
          {isFailure && (
            <button
              onClick={() => navigate("/payment")}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              Try Again
            </button>
          )}
          <button
            onClick={() => navigate("/landing")}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Go to Home
          </button>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Powered by ShurjoPay • Secure Payment Gateway
          </p>
        </div>
      </div>
    </div>
  );
}
